// PropertyMap.jsx
// Reusable Google Maps component for displaying property locations

import React, { useState } from "react";
import { GoogleMap, Marker, InfoWindow } from "@react-google-maps/api";
import { findNearbyPlaces } from "../../utils/geocoding";

const mapContainerStyle = {
  width: "100%",
  height: "500px",
  borderRadius: "12px",
};

const streetViewContainerStyle = {
  width: "100%",
  height: "400px",
  borderRadius: "12px",
  marginTop: "16px",
};

const defaultMapOptions = {
  zoomControl: true,
  streetViewControl: true,
  mapTypeControl: false,
  fullscreenControl: true,
  styles: [], // Standard roadmap style
};

export default function PropertyMap({
  center,
  zoom = 15,
  showNearbyAmenities = false,
  showStreetView = false,
  properties = [],
}) {
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [nearbyPlaces, setNearbyPlaces] = useState([]);
  const [showStreetViewPane, setShowStreetViewPane] = useState(showStreetView);

  // Fetch nearby amenities when component mounts
  React.useEffect(() => {
    if (showNearbyAmenities && center) {
      loadNearbyAmenities();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showNearbyAmenities, center]);

  const loadNearbyAmenities = async () => {
    const types = [
      { type: "school", radius: 1000 },
      { type: "transit_station", radius: 500 },
      { type: "park", radius: 1000 },
      { type: "supermarket", radius: 1000 },
    ];

    const allPlaces = [];

    for (const { type, radius } of types) {
      const places = await findNearbyPlaces(center, radius, type);
      allPlaces.push(
        ...places.map((place) => ({
          ...place,
          category: type,
        }))
      );
    }

    setNearbyPlaces(allPlaces);
  };

  const getMarkerIcon = (category) => {
    const icons = {
      school: "🏫",
      transit_station: "🚇",
      park: "🌳",
      supermarket: "🛒",
      property: "🏠",
    };

    return icons[category] || "📍";
  };

  return (
    <>
      {/* Street View Toggle Button */}
      <div className="mb-4 flex gap-2">
        <button
          onClick={() => setShowStreetViewPane(!showStreetViewPane)}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
            showStreetViewPane
              ? "bg-hn-red text-white"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
          }`}
        >
          {showStreetViewPane ? "Hide" : "Show"} Street View
        </button>
      </div>

      {/* Street View Image */}
      {showStreetViewPane && center && (
        <div style={streetViewContainerStyle} className="relative">
          <img
            src={`https://maps.googleapis.com/maps/api/streetview?size=800x400&location=${center.lat},${center.lng}&fov=90&pitch=0&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`}
            alt="Street View"
            className="w-full h-full object-cover rounded-xl"
            onError={(e) => {
              e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='800' height='400'%3E%3Crect width='800' height='400' fill='%23f3f4f6'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' dy='.3em' fill='%23666' font-family='system-ui' font-size='16'%3EStreet View not available for this location%3C/text%3E%3C/svg%3E";
            }}
          />
          <a
            href={`https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${center.lat},${center.lng}`}
            target="_blank"
            rel="noopener noreferrer"
            className="absolute bottom-4 right-4 px-4 py-2 bg-white text-gray-700 rounded-lg shadow-lg hover:bg-gray-50 transition text-sm font-medium"
          >
            Open in Google Maps
          </a>
        </div>
      )}

      {/* Google Map */}
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={center}
        zoom={zoom}
        options={defaultMapOptions}
      >
      {/* Property Markers */}
      {properties.map((property, index) => {
        if (!property.lat || !property.lng) return null;

        return (
          <Marker
            key={`property-${property.id || index}`}
            position={{ lat: property.lat, lng: property.lng }}
            title={property.name || property.address}
            icon={{
              url: `data:image/svg+xml,${encodeURIComponent(
                `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40"><text x="50%" y="50%" text-anchor="middle" dy=".3em" font-size="24">🏠</text></svg>`
              )}`,
            }}
            onClick={() => setSelectedProperty(property)}
          />
        );
      })}

      {/* Property InfoWindow */}
      {selectedProperty && selectedProperty.lat && selectedProperty.lng && (
        <InfoWindow
          position={{ lat: selectedProperty.lat, lng: selectedProperty.lng }}
          onCloseClick={() => setSelectedProperty(null)}
        >
          <div className="p-2 max-w-xs">
            <h3 className="font-semibold text-lg text-hn-dark">
              {selectedProperty.name}
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              {selectedProperty.address}
            </p>
            {selectedProperty.currentValue && (
              <p className="text-sm font-medium text-hn-primary mt-2">
                ${selectedProperty.currentValue.toLocaleString()}
              </p>
            )}
            {selectedProperty.type && (
              <p className="text-xs text-gray-500 mt-1">
                {selectedProperty.type}
                {selectedProperty.sqft && ` • ${selectedProperty.sqft} sq ft`}
              </p>
            )}
          </div>
        </InfoWindow>
      )}

      {/* Nearby Amenities Markers */}
      {showNearbyAmenities &&
        nearbyPlaces.map((place, index) => (
          <Marker
            key={`amenity-${index}`}
            position={{
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng(),
            }}
            title={place.name}
            icon={{
              url: `data:image/svg+xml,${encodeURIComponent(
                `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32"><text x="50%" y="50%" text-anchor="middle" dy=".3em" font-size="20">${getMarkerIcon(
                  place.category
                )}</text></svg>`
              )}`,
            }}
            opacity={0.7}
          />
        ))}
      </GoogleMap>
    </>
  );
}
